import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { ago, compact, count, day, delta, inr, inrCompact, num, qty } from '../lib/format.js';
import { ActivityBars, BarsH, Meter, StackedBars, TrendArea } from '../components/charts.jsx';
import { Alert, Card, CardSkeleton, Empty, Pill, Skeleton, StatCard, useAsync } from '../components/ui.jsx';
import AlertPanel from '../components/alerts.jsx';
import { ROLES, can } from '../lib/roles.js';
import { thisMonth } from '../lib/dates.js';
import PeriodPicker, { rangeLabel } from '../components/PeriodPicker.jsx';

/**
 * Answers three questions in order: are we all right (the cards), why (the
 * charts), and what needs doing now (the queue table). Every card is a link into
 * the filtered list behind the number.
 */
export default function Dashboard() {
  const session = useSession();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(thisMonth);
  const entityId = session.entities[0] ? session.entities[0].id : '';
  const a = useAsync(() => api.get(
    `/api/reports/analytics?from=${period.from}&to=${period.to}${entityId ? `&entity_id=${entityId}` : ''}`),
  [period.from, period.to, entityId]);
  const periodName = rangeLabel(period.from, period.to);

  const firstName = String(session.user.full_name || '').split(' ')[0];
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening';

  if (a.error) return <Alert onRetry={a.reload}>{a.error}</Alert>;

  if (a.loading && !a.data) {
    return (
      <>
        <div className="page-head"><Skeleton height={26} width={280} /></div>
        <div className="grid kpi" style={{ marginBottom: 16 }}>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="stat"><Skeleton height={12} width="55%" /><Skeleton height={30} width="70%" style={{ marginTop: 12 }} /></div>)}
        </div>
        <div className="grid thirds"><CardSkeleton lines={6} /><CardSkeleton lines={4} /></div>
      </>
    );
  }

  const { headline: h, pipeline: p, trend, top_items: topItems, stock_by_godown: stock, sync_activity: sync, oldest_open_orders: open } = a.data;

  const monthDelta = delta(h.mtd_value, h.prev_mtd_value);
  const todayDelta = delta(h.today_value, h.yesterday_value);
  const specTotal = num(p.spec_ready) + num(p.spec_blocked);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting}, {firstName}</h1>
          <div className="page-sub">
            {day(new Date())} · Tally last synced {ago(p.last_sync_at)} · every figure below opens the list behind it.
          </div>
        </div>
        <div className="inline">
          <PeriodPicker value={period} onChange={setPeriod} />
          {can(session, ROLES.COUNTER) && (
            <Link className="btn" to="/delivery-orders/new"><span aria-hidden="true">＋</span> New delivery order</Link>
          )}
        </div>
      </div>

      <AlertPanel compact />

      {/* ── are we all right? ── */}
      <div className="kpi-layout" style={{ marginBottom: 16, opacity: a.loading ? 0.6 : 1 }}>
        <StatCard hero
          label="Billed this month"
          value={inrCompact(h.mtd_value)}
          delta={monthDelta}
          foot={`${count(h.mtd_count)} invoices · ${inr(h.mtd_taxable, 0)} taxable`}
          spark={trend.map((t) => num(t.total))}
          to="/invoices?range=month&status=issued" />
        <div className="kpi-rest">
        <StatCard
          label="Billed today"
          value={inrCompact(h.today_value)}
          delta={todayDelta}
          foot={`${count(h.today_count)} invoices`}
          to="/invoices?range=today&status=issued" />
        <StatCard
          label="Open delivery orders"
          value={count(p.open_orders)}
          foot={`${compact(p.qty_pending)} units pending despatch`}
          to="/delivery-orders?status=issued,partially_delivered" />
        <StatCard
          label="Awaiting invoice"
          value={count(p.awaiting_invoice)}
          foot="despatched, not yet billed"
          tone={num(p.awaiting_invoice) > 0 ? 'attention' : undefined}
          to="/deliveries?status=confirmed" />
        <StatCard
          label="Tally queue"
          value={count(num(p.tally_pending) + num(p.tally_failed))}
          foot={num(p.tally_failed) > 0 ? `${p.tally_failed} failed — needs a look` : 'nothing failed'}
          tone={num(p.tally_failed) > 0 ? 'attention' : undefined}
          to="/sync" />
        <StatCard
          label="Average invoice"
          value={inrCompact(num(h.mtd_count) ? num(h.mtd_value) / num(h.mtd_count) : 0)}
          foot="this month, per invoice"
          to="/invoices?range=month&status=issued" />
        <StatCard
          label="Batches blocking billing"
          value={count(p.spec_blocked)}
          foot="in stock, properties not recorded"
          tone={num(p.spec_blocked) > 0 ? 'attention' : undefined}
          to="/batches?spec_status=pending" />
        </div>
      </div>

      {/* ── why? ── */}
      <div className="grid thirds" style={{ marginBottom: 16 }}>
        <Card title="Billing trend" note={`Invoiced value, ${periodName}`}
          actions={<span className="card-note num">{inr(trend.reduce((s, t) => s + num(t.total), 0), 0)} total</span>}>
          <TrendArea data={trend} height={300} caption="Billed value" />
        </Card>

        <div>
          <Card title="Specification readiness" note="Batches in stock">
            {specTotal === 0
              ? <Empty title="No stock in hand">Run a Tally sync to bring batch stock across.</Empty>
              : (
                <Meter
                  parts={[
                    { label: 'Ready to bill', value: num(p.spec_ready), color: 'var(--series-3)' },
                    { label: 'Blocked', value: num(p.spec_blocked), color: 'var(--critical)' },
                  ]}
                  caption={num(p.spec_blocked) > 0
                    ? 'A batch without recorded properties cannot go on an invoice.'
                    : 'Every batch in stock can be billed.'} />
              )}
            {num(p.spec_blocked) > 0 && (
              <Link className="btn small secondary" style={{ marginTop: 14 }} to="/batches?spec_status=pending">Record properties</Link>
            )}
          </Card>
          <Card title="Tally sync activity" note="Jobs completed, last 14 days">
            <ActivityBars data={sync} />
            <div className="card-note" style={{ marginTop: 10 }}>
              Last sync {ago(p.last_sync_at)}. <Link to="/sync">Open the sync console</Link>
            </div>
          </Card>
        </div>
      </div>

      <div className="grid halves" style={{ marginBottom: 16 }}>
        <Card title="What is selling" note={`Taxable value, ${periodName}`}>
          {topItems.length === 0
            ? <Empty title="Nothing billed in this period">Pick a longer range, or raise a delivery order.</Empty>
            : <BarsH data={topItems.map((t) => ({ label: t.item, value: num(t.value), sub: qty(t.qty, t.uom) }))} />}
        </Card>

        <Card title="Stock position by godown" note="Available against committed, from the last Tally sync">
          {stock.length === 0
            ? <Empty title="No godowns yet">Masters arrive with the first Tally sync.</Empty>
            : (
              <>
                <StackedBars
                  data={stock.map((s) => ({ label: s.godown, available: num(s.available), committed: num(s.committed) }))}
                  keys={[{ key: 'available', label: 'Available' }, { key: 'committed', label: 'Committed to despatch' }]}
                  onSelect={(d) => navigate(`/batches?godown=${encodeURIComponent(d.label)}`)} />
                <div className="card-note" style={{ marginTop: 12 }}>
                  {stock.filter((s) => num(s.batches_without_specs) > 0).map((s) => (
                    <div key={s.godown}>{s.godown}: {s.batches_without_specs} of {s.batches} batches without properties</div>
                  ))}
                </div>
              </>
            )}
        </Card>
      </div>

      {/* ── what needs doing? ── */}
      <Card title="Oldest open delivery orders" note="Raised, not yet despatched in full"
        actions={<Link className="btn small secondary" to="/delivery-orders?status=issued,partially_delivered">See all</Link>}>
        {open.length === 0
          ? <Empty title="Nothing is waiting at the godown">Every issued order has been despatched in full.</Empty>
          : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Order</th><th>Buyer</th><th>Godown</th><th className="r">Pending</th><th className="r">Age</th><th /></tr>
                </thead>
                <tbody>
                  {open.map((o) => (
                    <tr key={o.id} className="clickable"
                      onClick={() => navigate(can(session, ROLES.GODOWN) ? '/godown' : '/delivery-orders?status=issued,partially_delivered')}>
                      <td className="mono">{o.do_number}<div className="card-note">{day(o.do_date)}</div></td>
                      <td>{o.buyer_name}</td>
                      <td>{o.godown_name}</td>
                      <td className="r num">{compact(o.qty_pending)}</td>
                      <td className="r num">{num(o.age_days)} d</td>
                      <td className="r">
                        {num(o.age_days) >= 3
                          ? <Pill value="pending" label="ageing" />
                          : <Pill value="issued" label="on track" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </>
  );
}
